import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Gift } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { Seo } from "@/components/site/Seo";

export default function Join() {
  const { register, login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const ref = params.get("ref") || "";
  const [mode, setMode] = useState("register");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "register") await register(email, password, name, ref || undefined);
      else await login(email, password);
      navigate("/portal");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-ivory">
      <Seo title="Join | Psalm 30:2 Chiropractic" />
      <div className="hidden lg:block relative bg-espresso">
        <div className="absolute inset-0 bg-cover bg-center opacity-55 kenburns" style={{ backgroundImage: "url(https://images.unsplash.com/photo-1557446772-0985ac892643?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400)" }} />
        <div className="absolute inset-0 bg-espresso/55" />
        <div className="relative h-full flex flex-col justify-end p-16 text-ivory">
          <div className="font-serif text-5xl leading-tight">Care that<br />rewards you.</div>
          <div className="text-sm text-ivory/70 mt-5 max-w-sm font-light">Earn points on every visit, grow your family's Treasure Chest and unlock rewards when you refer friends.</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="join-form">
          {ref && (
            <div className="mb-6 flex items-center gap-3 bg-brass/10 border border-brass/30 rounded-xl px-4 py-3 text-sm text-espresso" data-testid="join-referral-banner">
              <Gift size={18} className="text-brass shrink-0" />
              A friend invited you — welcome! You're all set to book your first visit.
            </div>
          )}
          <div className="overline mb-4">{mode === "register" ? "Create your account" : "Welcome back"}</div>
          <h1 className="font-serif font-light text-4xl text-espresso mb-8">{mode === "register" ? "Join the rewards program" : "Sign in"}</h1>

          {error && <div className="mb-5 text-sm text-destructive bg-destructive/5 border border-destructive/20 rounded-sm px-4 py-3" data-testid="join-error">{error}</div>}

          {mode === "register" && (
            <>
              <label className="block text-xs uppercase tracking-[0.15em] text-taupe mb-2">Full name</label>
              <input data-testid="join-name" value={name} onChange={(e) => setName(e.target.value)} required
                className="w-full bg-ivory border border-espresso/20 rounded-md px-4 py-3 mb-5 focus:outline-none focus:ring-2 focus:ring-brass" />
            </>
          )}

          <label className="block text-xs uppercase tracking-[0.15em] text-taupe mb-2">Email</label>
          <input data-testid="join-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
            className="w-full bg-ivory border border-espresso/20 rounded-md px-4 py-3 mb-5 focus:outline-none focus:ring-2 focus:ring-brass" />

          <label className="block text-xs uppercase tracking-[0.15em] text-taupe mb-2">Password</label>
          <input data-testid="join-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8}
            className="w-full bg-ivory border border-espresso/20 rounded-md px-4 py-3 mb-2 focus:outline-none focus:ring-2 focus:ring-brass" />
          {mode === "register" && <p className="text-xs text-taupe mb-6">At least 8 characters.</p>}
          {mode === "login" && <div className="mb-6" />}

          <button data-testid="join-submit" type="submit" disabled={loading}
            className="w-full bg-espresso text-ivory py-3.5 rounded-full text-xs uppercase tracking-[0.2em] hover:bg-brass transition-all disabled:opacity-60">
            {loading ? "Please wait…" : mode === "register" ? "Create Account" : "Sign In"}
          </button>

          <button type="button" data-testid="join-toggle" onClick={() => { setMode(mode === "register" ? "login" : "register"); setError(""); }}
            className="mt-6 w-full text-center text-sm text-taupe hover:text-espresso transition-colors">
            {mode === "register" ? "Already have an account? Sign in" : "New here? Create an account"}
          </button>
        </form>
      </div>
    </div>
  );
}
